import React, { useState, useEffect } from "react";
import Sidebar from "../../../components/AdminComponents/SideNav";
import { SettingsMenu } from "../../../components/AdminComponents/MenuItems";
import CommonTopDetails from "../../../components/AdminComponents/CommonTopDetails";
import { Table, Spin, Pagination, Modal, Input, message, Button } from "antd";
import { DeleteTwoTone, WarningOutlined } from "@ant-design/icons";
import Axios from "../../../api/Axios";

const TrainerySuppression = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedSearch !== search) {
        setDebouncedSearch(search);
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [search, debouncedSearch]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const showModal = (record) => {
    setDeleteRecord(record);
    setConfirmText("");
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setDeleteRecord(null);
    setConfirmText("");
  };

  function getCookie(name) {
    const cookies = document.cookie.split("; ");
    for (let i = 0; i < cookies.length; i++) {
      const [cookieName, cookieValue] = cookies[i].split("=");
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  }

  const handleDelete = async () => {
    if (confirmText !== "CONFIRM DELETE") return;
    setIsDeleting(true);
    const csrfToken = getCookie("csrftoken");
    try {
      await Axios.post(
        "/sessuppression/remove/",
        {
          email: deleteRecord.EmailAddress,
        },
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );
      message.success("Successfully deleted");
      setIsModalOpen(false);
      setDeleteRecord(null);
      LoadData();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  };

  const LoadData = async () => {
    setLoading(true);
    try {
      const res = await Axios.get(`/sessuppression/list/?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearch}`);
      setData(res.data.results || res.data);
      setTotalItems(res.data.count || res.data.length || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    LoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearch]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const onShowSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      title: "Email Address",
      dataIndex: "EmailAddress",
      key: "EmailAddress",
    },
    {
      title: "Reason",
      dataIndex: "Reason",
      key: "Reason",
    },
    {
      title: "Last Update Date",
      dataIndex: "LastUpdateTime",
      key: "LastUpdateTime",
      render: (text) => {
        if (!text) return "";
        const date = new Date(text);
        const month = date.toLocaleString("default", { month: "short" });
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month}-${day < 10 ? "0" + day : day}-${year}`;
      },
    },
    {
      title: "Actions",
      key: "action",
      align: "center",
      render: (text, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <DeleteTwoTone
            style={{ cursor: "pointer", fontSize: "1.5rem" }}
            title="delete"
            twoToneColor="#eb2f96"
            onClick={() => showModal(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <Sidebar props={SettingsMenu}>
      <div>
        <CommonTopDetails
          title="Trainery Suppression"
          titleSuffix={`${totalItems || 0} record(s)`}
          searchPlaceholder="Search by Email..."
          searchAction={handleSearchChange}
        />
        <div className="form-container-3" style={{ padding: "1rem" }}>
          <Spin spinning={loading}>
            <Table
              size="small"
              rowKey="EmailAddress"
              dataSource={data}
              columns={columns}
              bordered
              className="table-container"
              pagination={false}
              style={{ margin: "1rem" }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "1rem",
              }}
            >
              <Pagination
                size="small"
                showSizeChanger
                onShowSizeChange={onShowSizeChange}
                onChange={onPageChange}
                pageSizeOptions={["10", "15", "30", "50", "100"]}
                showQuickJumper
                current={currentPage}
                defaultPageSize={pageSize}
                total={totalItems}
              />
            </div>
          </Spin>
        </div>
      </div>
      <Modal
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        closable={false}
        width={400}
        bodyStyle={{ padding: "10px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "10px" }}>
          <div
            style={{
              backgroundColor: "#fce8e8",
              padding: "10px 14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <WarningOutlined style={{ color: "#d9363e", fontSize: "24px" }} />
          </div>
          <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "12px", textAlign: "center", color: "#222" }}>
            Delete email "{deleteRecord?.EmailAddress}"?
          </h3>
          <p style={{ color: "#666", textAlign: "center", fontSize: "13px", marginBottom: "24px", lineHeight: "1.5" }}>
            This action cannot be undone. This will permanently delete the email suppression, including all associated history.
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <p style={{ fontSize: "12px", fontWeight: 700, marginBottom: "8px", color: "#444" }}>
            To confirm, type "CONFIRM DELETE" in the box below:
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            placeholder="Type here..."
            autoComplete="off"
            size="large"
            style={{ backgroundColor: "#f9f9fa", borderColor: "#e0e0e0" }}
          />
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <Button
            size="large"
            style={{ flex: 1, borderColor: "#d9d9d9", color: "#333", fontWeight: 500 }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            style={{ flex: 1, backgroundColor: confirmText === "CONFIRM DELETE" ? "#d73c3cff" : "#e8c8c8", borderColor: "transparent", color: "#fff", fontWeight: 500 }}
            disabled={confirmText !== "CONFIRM DELETE"}
            loading={isDeleting}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </Sidebar>
  );
};

export default TrainerySuppression;
